import { useState } from "react";
import type React from "react";
import { AlertTriangle, Check as CheckIcon, Circle, ListChecks, Plus, X } from "lucide-react";
import { theme, card, countTag, btnPrimary, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { PortalLink } from "../../components/PortalLink";
import { NextSteps } from "../../components/NextSteps";
import { Field, input, textarea, secondaryBtn, StatusPill, fromInput, relativeDue } from "../resilience/shared";
import { fmtDate as fmtDay, fmtDateTime } from "../../lib/format";
import { fmtDateOnly } from "../../lib/dateOnly";

// ── Types (mirror backend/src/routes/dora.routes.ts) ──────────────────────────

export type TriVal = "yes" | "no" | "unknown";
export type Answers = Partial<Record<string, TriVal>>;
export type Verdict = "major" | "not_major" | "undetermined";
export type Stage = "classify" | "initial" | "intermediate" | "final" | "close" | "done" | "not_reportable";

export interface Criterion { key: string; article: string; label: string; help: string; role: "gate" | "route_a" | "threshold" | "recurring" }
export interface Authority { key: string; name: string; kind: string; country: string; basis: string }
export interface CtxAsset { id: string; name: string; kind: string; criticality: string; isEssentialFunction: boolean }
export interface CtxSupplier { id: string; name: string; category: string | null; riskTier: string; status: string }
export interface CtxRisk { id: string; displayId: number | null; title: string; residualScore: number | null }

export interface DoraContext {
  authorities: Authority[];
  assets: CtxAsset[];
  suppliers: CtxSupplier[];
  risks: CtxRisk[];
  classification: { criteria: Criterion[]; stages: Record<string, string> };
}

export interface AlertLite { entityId: string | null; kind: string; severity: "warning" | "breach" | string; title: string; detail?: string; dueAt: string | null }
export interface ActionLite { id: string; displayId: number | null; title: string; status: string; sourceRef: string | null }

export interface Clock { dueAt: string; submittedAt: string | null; state: "on_time" | "late" | "overdue" | "due" }

export interface ClassificationResult { verdict: Verdict; met: string[]; missing: string[]; reasoning: string }

export interface DoraIncident {
  id: string;
  title: string;
  description: string;
  detectedAt: string;
  /** When the entity became aware (RTS 2025/301 Art. 5(1)(a)); null = detection. */
  awareAt?: string | null;
  classifiedAt: string | null;
  isMajor: boolean;
  initialNotifiedAt: string | null;
  intermediateReportAt: string | null;
  /** The latest updated intermediate report (Art. 5(1)(b)); the final report is due a month after it. */
  updatedIntermediateReportAt?: string | null;
  finalReportAt: string | null;
  status: "OPEN" | "MONITORING" | "CLOSED" | string;
  competentAuthority: string | null;
  authorityKey: string | null;
  affectedClientsCount: number | null;
  geographicScope: string | null;
  notes: string | null;
  rootCause: string | null;
  lessonsLearned: string | null;
  initialReference: string | null;
  intermediateReference: string | null;
  finalReference: string | null;
  evidenceDocumentId: string | null;
  classification: Answers;
  classificationResult: ClassificationResult;
  stage: Stage;
  clocks: { initial: Clock | null; intermediate: Clock | null; final: Clock | null } | null;
  alerts: AlertLite[];
  correctiveActions: ActionLite[];
  gaps: string[];
  linkedRisk: { id: string; title: string; displayId: number | null; status: string } | null;
  supplier: { id: string; name: string; riskTier: string } | null;
  assets: Array<{ asset: { id: string; name: string; kind: string; criticality: string; isEssentialFunction: boolean } }>;
  securityIncidents: Array<{ id: string; displayId: number | null; title: string; status: string; severity: string }>;
  linkedRiskId: string | null;
  supplierId: string | null;
}

export interface DoraTest {
  id: string;
  testType: string;
  scheduledDate: string;
  completedDate: string | null;
  provider: string | null;
  scope: string | null;
  findingsCount: number | null;
  criticalFindings: number | null;
  status: "SCHEDULED" | "IN_PROGRESS" | "COMPLETED" | "OVERDUE" | string;
  notes: string | null;
  evidenceDocumentId: string | null;
  assets: Array<{ assetId: string; asset: { id: string; name: string; criticality: string; isEssentialFunction: boolean } }>;
  alerts: AlertLite[];
  correctiveActions: ActionLite[];
  /** The resilience exercise this test was run as; its findings and their actions live there. */
  exerciseId: string | null;
  exercise: TestExercise | null;
}

export interface TestExercise {
  id: string;
  title: string;
  kind: string;
  status: string;
  completedDate: string | null;
  outcome: "met" | "partially_met" | "not_met" | null;
  findingsCount: number | null;
  criticalFindings: number | null;
  findingRows: Array<{ id: string; title: string; severity: string; correctiveAction: { id: string; displayId: number | null; title: string; status: string } | null }>;
}

export interface TestsResponse {
  tests: DoraTest[];
  programmeAlerts: AlertLite[];
  coverage: { important: number; testedInYear: number; untested: Array<{ id: string; name: string; criticality: string; isEssentialFunction: boolean }> };
}

export interface DoraThirdParty {
  id: string;
  name: string;
  serviceType: string;
  isCritical: boolean;
  riskTier: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL" | string;
  contractInPlace: boolean;
  lastDueDiligence: string | null;
  nextReviewDate: string | null;
  slaStatus: "MET" | "BREACHED" | "UNKNOWN" | string;
  auditRightExercised: boolean;
  exitPlanInPlace: boolean;
  notes: string | null;
  supplierId: string | null;
  supplier: { id: string; name: string; riskTier: string; status: string } | null;
  supplierSummary: {
    contractsInForce: number;
    contracts: number;
    latestScore: number | null;
    assets: Array<{ id: string; name: string; criticality: string; isEssentialFunction: boolean }>;
    registerTier: string;
    status: string;
  } | null;
  reconcile: string[];
  /** Where this row and the supplier register now differ (backend lib/recordConsistency.ts thirdPartySupplierDiff). */
  comparison: ThirdPartyComparison[];
  gaps: string[];
  alerts: AlertLite[];
  // Register of Information (Art. 28(3)) — backend/src/lib/doraRegisterOfInformation.ts
  lei: string | null;
  providerCountry: string | null;
  functionSupported: string | null;
  substitutability: "easy" | "medium" | "difficult" | "impossible" | null;
  dataLocations: string[];
  dataSensitivity: "low" | "medium" | "high" | null;
  /** null = not answered; [] = no ICT subcontractors. */
  subcontractors: Subcontractor[] | null;
  contractStartDate: string | null;
  contractEndDate: string | null;
  annualCostEur: number | null;
  exitStrategy: string | null;
  exitTestedAt: string | null;
  roiMissing: Array<{ template: string; field: string; message: string }>;
  concentration: Concentration | null;
}

export type ThirdPartyField = "name" | "riskTier" | "contractInPlace" | "contractStartDate" | "contractEndDate" | "lastDueDiligence" | "nextReviewDate" | "slaStatus";
export type SyncDirection = "from_supplier" | "to_supplier";

/** One field the two registers disagree on; dates as yyyy-mm-dd. sync null = a check for a person, not a sync. */
export interface ThirdPartyComparison {
  field: ThirdPartyField;
  label: string;
  dora: string | boolean | null;
  supplier: string | boolean | null;
  sync: SyncDirection | "either" | null;
  note: string | null;
}

export interface Subcontractor { name: string; lei?: string | null; country?: string | null; service: string }

export interface Concentration {
  providerId: string;
  providerName: string;
  functions: string[];
  criticalFunctions: number;
  criticalAssets: number;
  incidents: number;
  concentrated: boolean;
  reason: string | null;
}

export interface RoiCompleteness { filled: number; required: number; percent: number }

export interface RoiSummary {
  generatedAt: string;
  completeness: RoiCompleteness;
  templates: Array<{ code: string; title: string; rows: number; completeness: RoiCompleteness }>;
  missing: Array<{ template: string; providerId: string | null; providerName: string | null; field: string; message: string }>;
  concentrated: Concentration[];
}

export interface ThirdPartiesResponse {
  thirdParties: DoraThirdParty[];
  unregisteredSuppliers: Array<{ id: string; name: string; category: string | null; riskTier: string }>;
  registerOfInformation?: RoiSummary;
}

export const SUBSTITUTABILITY_OPTIONS: Array<{ id: string; label: string }> = [
  { id: "easy", label: "Easily substitutable" },
  { id: "medium", label: "Medium difficulty" },
  { id: "difficult", label: "Highly complex" },
  { id: "impossible", label: "Not substitutable" },
];

/**
 * ISO 17442 LEI check (mirror of backend isValidLei): 18 alphanumerics plus two check digits,
 * the whole string satisfying MOD 97-10. The server validates again on save.
 */
export function isValidLei(raw: string | null | undefined): boolean {
  const lei = (raw ?? "").trim().toUpperCase();
  if (!/^[A-Z0-9]{18}[0-9]{2}$/.test(lei)) return false;
  let rem = 0;
  for (const ch of lei) {
    const digits = /[0-9]/.test(ch) ? ch : String(ch.charCodeAt(0) - 55);
    for (const d of digits) rem = (rem * 10 + Number(d)) % 97;
  }
  return rem === 1;
}

// ── Catalogues (mirror backend/src/lib/doraMonitorRules.ts) ───────────────────

export const TEST_TYPES: Array<{ id: string; label: string }> = [
  { id: "VULNERABILITY_SCAN", label: "Vulnerability assessment and scan" },
  { id: "OPEN_SOURCE", label: "Open-source analysis" },
  { id: "NETWORK_SECURITY", label: "Network security assessment" },
  { id: "SCENARIO_BASED", label: "Scenario-based test" },
  { id: "CONTINUITY_COMPAT", label: "ICT continuity compatibility test" },
  { id: "PENETRATION_TEST", label: "Penetration test" },
  { id: "TLPT", label: "Threat-led penetration test (TLPT)" },
];
export const REQUIRED_TEST_TYPES = ["VULNERABILITY_SCAN", "OPEN_SOURCE", "NETWORK_SECURITY", "SCENARIO_BASED", "CONTINUITY_COMPAT", "PENETRATION_TEST"];
export const testTypeLabel = (id: string) => TEST_TYPES.find((t) => t.id === id)?.label ?? id.toLowerCase().replace(/_/g, " ");

export const CONTROL_LABELS: Record<string, string> = {
  "legal-dora-art17": "ICT incident management and reporting (Arts. 17–23)",
  "legal-dora-art24": "Digital operational resilience testing (Arts. 24–27)",
  "legal-dora-art28": "ICT third-party risk (Arts. 28–44)",
};

export const HOUR = 3_600_000;
export const DAY = 24 * HOUR;

// ── Classification (port of backend/src/lib/doraClassification.ts classifyIncident) ──
// Kept identical so the verdict shown while answering is the one the server stores on save.
// Unknown never exempts: an unanswered criterion that could still make it major leaves it undetermined.

export function classify(a: Answers, criteria: Criterion[]): ClassificationResult {
  const val = (k: string): TriVal => a[k] ?? "unknown";
  const thresholdKeys = criteria.filter((c) => c.role === "threshold").map((c) => c.key);
  const label = (k: string) => criteria.find((c) => c.key === k)?.label.toLowerCase() ?? k;
  const met: string[] = [];
  for (const k of ["criticalServices", "maliciousAccess", ...thresholdKeys, "recurring"]) if (val(k) === "yes") met.push(k);

  if (val("recurring") === "yes") {
    return { verdict: "major", met, missing: [], reasoning: "Recorded as a recurring incident that together meets the classification thresholds (RTS Art. 16): treat as major." };
  }
  const gate = val("criticalServices");
  if (gate === "no") {
    return { verdict: "not_major", met, missing: [], reasoning: "Critical or important services were not affected (RTS Art. 6), which every major incident requires (Art. 8(1)(a))." };
  }
  const thresholdsMet = thresholdKeys.filter((k) => val(k) === "yes");
  const thresholdsUnknown = thresholdKeys.filter((k) => val(k) === "unknown");
  const routeA = val("maliciousAccess");
  const routeB = thresholdsMet.length >= 2;
  const couldRouteB = thresholdsMet.length + thresholdsUnknown.length >= 2;

  if (gate === "yes" && (routeA === "yes" || routeB)) {
    const why = routeA === "yes"
      ? "critical services were affected and there was malicious unauthorised access that may result in data losses (RTS Art. 8(1)(b)(i))"
      : `critical services were affected and ${thresholdsMet.length} materiality thresholds are met — ${thresholdsMet.map(label).join(", ")} (RTS Art. 8(1)(b)(ii))`;
    return { verdict: "major", met, missing: [], reasoning: `Major: ${why}.` };
  }
  const couldBeMajor = routeA !== "no" || couldRouteB;
  if (!couldBeMajor) {
    return { verdict: "not_major", met, missing: [], reasoning: `Not major: ${thresholdsMet.length === 0 ? "no materiality threshold is met" : `only ${thresholdsMet.length} materiality threshold is met (${thresholdsMet.map(label).join(", ")}), two are needed`} and there was no malicious unauthorised access (RTS Art. 8).` };
  }
  const missing: string[] = [];
  if (gate === "unknown") missing.push("criticalServices");
  if (routeA === "unknown") missing.push("maliciousAccess");
  if (!routeB) missing.push(...thresholdsUnknown);
  return {
    verdict: "undetermined", met, missing,
    reasoning: `Cannot rule out a major incident yet: ${missing.map(label).join(", ")} ${missing.length === 1 ? "is" : "are"} unanswered. Until they are, treat it as possibly major — the 24-hour awareness clock is running.`,
  };
}

// ── Formatting ────────────────────────────────────────────────────────────────

/** "3h 20m", "5 days" — the same wording as the monitor's alerts. */
export function span(ms: number): string {
  const abs = Math.abs(ms);
  const hours = Math.floor(abs / HOUR);
  if (hours < 48) {
    const minutes = Math.floor((abs % HOUR) / 60_000);
    return hours > 0 ? `${hours}h ${minutes}m` : `${Math.max(1, minutes)}m`;
  }
  const days = Math.floor(abs / DAY);
  return `${days} day${days === 1 ? "" : "s"}`;
}

export function clockText(c: Clock): string {
  if (c.state === "on_time") return "done on time";
  if (c.state === "late") return "done late";
  const ms = new Date(c.dueAt).getTime() - Date.now();
  return c.state === "overdue" || ms < 0 ? `overdue by ${span(ms)}` : `due in ${span(ms)}`;
}

export const riskRef = (r: { displayId: number | null; title: string }) => `${r.displayId != null ? `RISK-${String(r.displayId).padStart(3, "0")} ` : ""}${r.title}`;
export const actionRef = (a: { displayId: number | null }) => (a.displayId != null ? `CA-${a.displayId}` : "CA");

const CRIT_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
export const byCriticality = <T extends { criticality: string; isEssentialFunction?: boolean; name: string }>(a: T, b: T) =>
  (CRIT_ORDER[a.criticality] ?? 9) - (CRIT_ORDER[b.criticality] ?? 9) || Number(!!b.isEssentialFunction) - Number(!!a.isEssentialFunction) || a.name.localeCompare(b.name);

// ── Small UI pieces ───────────────────────────────────────────────────────────

export type Tone = "success" | "warning" | "danger" | "accent" | "neutral";

export const TONE: Record<Tone, { bg: string; color: string; border: string }> = {
  success: { bg: theme.color.successBg, color: theme.color.successText, border: theme.color.success },
  warning: { bg: theme.color.warningBg, color: theme.color.warningText, border: theme.color.warning },
  danger:  { bg: theme.color.dangerBg,  color: theme.color.dangerText,  border: theme.color.danger },
  accent:  { bg: theme.color.accentBg,  color: theme.color.accentText,  border: theme.color.accent },
  neutral: { bg: theme.color.panelAlt,  color: theme.color.muted,       border: theme.color.borderStrong },
};

export function Pill({ tone, children, title }: { tone: Tone; children: React.ReactNode; title?: string }) {
  const t = TONE[tone];
  return <span title={title} style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: t.bg, color: t.color, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4 }}>{children}</span>;
}

export function VerdictPill({ verdict }: { verdict: Verdict }) {
  if (verdict === "major") return <Pill tone="danger">Major</Pill>;
  if (verdict === "not_major") return <Pill tone="success">Not major</Pill>;
  return <Pill tone="warning" title="Not yet classified: treated as possibly major until the criteria are answered">Possibly major</Pill>;
}

export const INCIDENT_STATUS: Record<string, { tone: Tone; label: string }> = {
  OPEN: { tone: "danger", label: "Open" },
  MONITORING: { tone: "warning", label: "Monitoring" },
  CLOSED: { tone: "success", label: "Closed" },
};

export const TEST_STATUS: Record<string, { tone: Tone; label: string }> = {
  SCHEDULED: { tone: "accent", label: "Scheduled" },
  IN_PROGRESS: { tone: "warning", label: "In progress" },
  COMPLETED: { tone: "success", label: "Completed" },
  OVERDUE: { tone: "danger", label: "Overdue" },
};

export const TIER_TONE: Record<string, Tone> = { CRITICAL: "danger", HIGH: "danger", MEDIUM: "warning", LOW: "success", critical: "danger", high: "danger", medium: "warning", low: "success" };

export function TierChip({ tier }: { tier: string }) {
  return <Pill tone={TIER_TONE[tier] ?? "neutral"}>{tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase()}</Pill>;
}

export function YesNo({ value, title }: { value: boolean; title?: string }) {
  return value
    ? <span title={title} style={{ display: "inline-flex", color: theme.color.successText }}><CheckIcon size={15} aria-label="Yes" /></span>
    : <span title={title} style={{ display: "inline-flex", color: theme.color.dangerText }}><X size={15} aria-label="No" /></span>;
}

export function Section({ title, icon, children, right, help }: { title: string; icon?: React.ReactNode; children: React.ReactNode; right?: React.ReactNode; help?: React.ReactNode }) {
  return (
    // A drawing sheet with a ruled title strip, the same frame as components/RecordView DetailSection.
    <section style={{ ...card, padding: 0, minWidth: 0 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", padding: "8px 14px", borderBottom: "1px solid var(--c-line)", minHeight: 36 }}>
        <h3 style={{ ...text.sectionTitle, margin: 0, display: "flex", alignItems: "center", gap: 7 }}>{icon}{title}</h3>
        {right}
      </header>
      <div style={{ padding: 14 }}>
        {help && <p style={{ ...text.hint, margin: "0 0 12px", maxWidth: 760 }}>{help}</p>}
        {children}
      </div>
    </section>
  );
}

export function ErrorText({ error }: { error: string | null }) {
  if (!error) return null;
  return <div style={{ ...text.hint, color: theme.color.dangerText, marginTop: 8 }}>{error}</div>;
}

export function SavedText({ show }: { show: boolean }) {
  if (!show) return null;
  return <span style={{ ...text.hint, color: theme.color.successText, display: "inline-flex", alignItems: "center", gap: 4 }}><CheckIcon size={12} />Saved</span>;
}

/** One busy flag, one error and one "saved" flash for a section of a form. */
export function useSaver() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  async function run(fn: () => Promise<unknown>, fallback = "Could not save") {
    setBusy(true); setError(null); setSaved(false);
    try { await fn(); setSaved(true); window.setTimeout(() => setSaved(false), 2500); return true; }
    catch (e) { setError(apiErrorMessage(e, fallback)); return false; }
    finally { setBusy(false); }
  }
  return { busy, error, saved, run, setError };
}

/** The monitor's open alerts about one record (or the programme), breach first. */
export function AlertList({ alerts, onOpen }: { alerts: AlertLite[]; onOpen?: (a: AlertLite) => void }) {
  if (alerts.length === 0) return null;
  const sorted = [...alerts].sort((a, b) => (a.severity === "breach" ? 0 : 1) - (b.severity === "breach" ? 0 : 1));
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {sorted.map((a, idx) => {
        const t = TONE[a.severity === "breach" ? "danger" : "warning"];
        const body = (
          <div style={{ display: "flex", gap: 10, padding: "10px 12px", borderRadius: 3, background: t.bg, border: `1px solid ${t.border}`, alignItems: "flex-start" }}>
            <AlertTriangle size={15} style={{ color: t.color, flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...text.itemTitle, color: t.color }}>{a.title}</div>
              {a.detail && <div style={{ ...text.hint, color: theme.color.text, marginTop: 2 }}>{a.detail}</div>}
            </div>
            {a.dueAt && <span style={{ ...text.meta, fontWeight: 600, color: t.color, whiteSpace: "nowrap" }}>{relativeDue(a.dueAt)}</span>}
          </div>
        );
        return onOpen
          ? <button key={`${a.kind}-${idx}`} type="button" onClick={() => onOpen(a)} style={{ all: "unset", cursor: "pointer", display: "block" }}>{body}</button>
          : <div key={`${a.kind}-${idx}`}>{body}</div>;
      })}
    </div>
  );
}

/** "What's missing" — the server's gaps list, each one a thing to do. */
export function GapsChecklist({ gaps, title = "What's missing" }: { gaps: string[]; title?: string }) {
  if (gaps.length === 0) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 3, background: theme.color.successBg, ...text.body, color: theme.color.successText, fontWeight: 600 }}>
        <CheckIcon size={15} /> Nothing missing — this record is complete.
      </div>
    );
  }
  // The same checklist every other record view carries (components/NextSteps.tsx).
  return <NextSteps title={title} items={gaps.map((g) => ({ done: false, label: g }))} />;
}

export function CountBadge({ n, tone, title }: { n: number; tone: Tone; title?: string }) {
  if (n <= 0) return null;
  const t = TONE[tone];
  // The tone stays in the ink so a danger count still reads as one.
  return <span title={title} style={{ ...countTag, ...(tone === "neutral" || tone === "accent" ? {} : { color: t.color, borderColor: t.border }) }}>{n}</span>;
}

export function FilterChips<T extends string>({ value, options, onChange }: { value: T; options: Array<{ id: T; label: string }>; onChange: (v: T) => void }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button key={o.id} type="button" onClick={() => onChange(o.id)} style={{ ...secondaryBtn, background: on ? "var(--c-accentBg)" : "transparent", color: on ? "var(--c-accentText)" : theme.color.muted, borderColor: on ? theme.color.accent : theme.color.borderStrong, fontWeight: on ? 600 : 400 }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600, whiteSpace: "nowrap" };
export const td: React.CSSProperties = { padding: "10px 14px", verticalAlign: "top" };
// Column headers read as field labels: sentence case, never uppercase.
export const theadRow: React.CSSProperties = { textAlign: "left", color: text.label.color, fontSize: text.label.fontSize, fontWeight: text.label.fontWeight };

export function dueTone(iso: string | null, warnDays = 30): Tone {
  if (!iso) return "neutral";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < 0) return "danger";
  if (ms < warnDays * DAY) return "warning";
  return "neutral";
}

export function DateCell({ iso, warnDays }: { iso: string | null; warnDays?: number }) {
  if (!iso) return <span style={{ color: theme.color.faint }}>—</span>;
  const tone = dueTone(iso, warnDays);
  return <span style={{ color: tone === "neutral" ? theme.color.muted : TONE[tone].color, fontWeight: tone === "neutral" ? 400 : 600, whiteSpace: "nowrap" }}>{fmtDateOnly(iso)}</span>;
}

// ── Corrective actions raised from a DORA record ──────────────────────────────

/** The corrective actions already raised from a record: information, shown in the record view. */
export function CorrectiveActionList({ actions, intro }: { actions: ActionLite[]; intro?: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {intro}
      {actions.length === 0 ? (
        <div style={{ ...text.body, color: theme.color.muted }}>No corrective actions raised from this record yet.</div>
      ) : (
        <div style={{ display: "grid", gap: 6 }}>
          {actions.map((a) => (
            <PortalLink key={a.id} to={`/improvement?action=${a.id}`} style={{ textDecoration: "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 3, border: "1px solid var(--c-border)", background: theme.color.panelAlt }}>
                <span style={{ ...text.ref, color: theme.color.muted }}>{actionRef(a)}</span>
                <span style={{ ...text.body, flex: 1, minWidth: 0 }}>{a.title}</span>
                <StatusPill value={a.status} />
              </div>
            </PortalLink>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * "Raise a corrective action": a state-changing action, so it sits in the record view's
 * Actions block. Closed it is one button; open, the short form takes the full row.
 */
export function RaiseCorrectiveAction({ source, sourceRef, defaultTitle, defaultPriority = "high", assetOptions, onCreated }: {
  source: "incident" | "exercise";
  sourceRef: string;
  defaultTitle: string;
  defaultPriority?: "critical" | "high" | "medium" | "low";
  assetOptions?: Array<{ id: string; name: string }>;
  onCreated: () => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState(defaultPriority);
  const [dueDate, setDueDate] = useState("");
  const [assetId, setAssetId] = useState("");
  const saver = useSaver();

  async function create() {
    if (!title.trim()) { saver.setError("Give the action a title"); return; }
    const ok = await saver.run(async () => {
      await api.post("/improvement/actions", {
        title: title.trim(), description: description.trim() || null, source, sourceRef, priority,
        dueDate: fromInput(dueDate), assetId: assetId || null,
      });
      await onCreated();
    }, "Could not raise the corrective action");
    if (ok) { setOpen(false); setDescription(""); setDueDate(""); setAssetId(""); }
  }

  if (!open) {
    return <button type="button" style={secondaryBtn} onClick={() => { setTitle(defaultTitle); setPriority(defaultPriority); setOpen(true); }}><Plus size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Raise a corrective action</button>;
  }
  return (
    <div style={{ ...card, padding: 14, boxShadow: "none", display: "grid", gap: 10, width: "100%", boxSizing: "border-box" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
        <Field label="What needs doing" span2><input style={input} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} /></Field>
        <Field label="Detail" span2><textarea style={textarea} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        <Field label="Priority">
          <select style={input} value={priority} onChange={(e) => setPriority(e.target.value as typeof priority)}>
            {(["critical", "high", "medium", "low"] as const).map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
        </Field>
        <Field label="Due by"><input type="date" style={input} value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></Field>
        {assetOptions && assetOptions.length > 0 && (
          <Field label="System it fixes (optional)">
            <select style={input} value={assetId} onChange={(e) => setAssetId(e.target.value)}>
              <option value="">—</option>
              {assetOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" style={btnPrimary} disabled={saver.busy} onClick={create}>{saver.busy ? "Raising…" : "Raise action"}</button>
        <button type="button" style={secondaryBtn} onClick={() => setOpen(false)}>Cancel</button>
      </div>
      <div style={text.meta}>It goes on the improvement programme, linked back to this record, and is tracked there to verification.</div>
      <ErrorText error={saver.error} />
    </div>
  );
}

/** Tiny "Last run 3h ago" etc. */
export function sinceText(iso: string | null): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  return ms < 60_000 ? "just now" : `${span(ms)} ago`;
}

export { fmtDay, fmtDateTime };
